import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import LogoutCookieProvider from '@peloton/auth/LogoutCookieProvider';
import {
  APPLE_LOGIN_ERROR_CODE,
  GOOGLE_LOGIN_ERROR_CODE,
} from '@peloton/auth/OauthProvider';
import { white } from '@engage/colors/base';
import { ErrorProvider, useError } from '../components';
import OauthProvider, { useAuth } from '../components/auth/OauthProvider';
import { getPersistedAuthData } from '../components/auth/persistAuthData';
import DarkLayout from '../components/DarkLayout/DarkLayout';
import LoadingSpinner from '../components/loading/LoadingSpinner';
import ViewProvider, { useView } from '../components/ViewProvider';
import type { ScreenHint } from '../components/ViewProvider';
import { useCopy } from '../copy';
import { useIsSsoOnRemoteActivationEnabled } from '../hooks/useIsSSOOnRemoteActivationEnabled';
import { Provider as OptimizelyProvider } from '../optimizely';

const ActivationView: React.FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter();

  const { ViewComponent, currentView, setView } = useView();

  if (router.pathname === '/email/unverified') {
    setView('Unverified');
  }

  if (router.pathname === '/email') {
    setView('EmailVerification');
    // note: do not use code or codes as a param, it will break
  }

  if (router.pathname === `/email/on-registration`) {
    setView('EmailVerificationOnRegistration');
  }

  if (currentView === 'Loading') {
    return (
      <DarkLayout>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <LoadingSpinner size={40} isLoading={true} fill={white} />
        </div>
      </DarkLayout>
    );
  }

  return (
    <DarkLayout removeBottomMargin={currentView === 'EmailVerificationOnRegistration'}>
      <ViewComponent />
    </DarkLayout>
  );
};

// Headless route guard to run side-effects under OauthProvider and before views render
const ActivationRouteOnError: React.FC = () => {
  const { verifyDevice, authEnv } = useAuth();
  const { setErrorCode, setErrorMessage, errorCode } = useError();
  const invalidCodeError = useCopy('remoteActivation.enterCodeInvalid') as string;
  const SsoDecision = useIsSsoOnRemoteActivationEnabled();

  useEffect(() => {
    if (typeof window === 'undefined' || !SsoDecision.enabled) {
      return;
    }

    const verifyAndRoute = async () => {
      if (
        errorCode &&
        (errorCode === GOOGLE_LOGIN_ERROR_CODE || errorCode === APPLE_LOGIN_ERROR_CODE)
      ) {
        const { action, userCode } = getPersistedAuthData(authEnv);
        if (action === 'device_activation' && !!userCode) {
          try {
            setErrorCode('');
            setErrorMessage('');
            await verifyDevice(userCode, { errorCode: errorCode });
          } catch (e) {
            setErrorMessage(invalidCodeError);
          }
        }
      }
    };
    verifyAndRoute();
  }, [
    errorCode,
    SsoDecision.enabled,
    authEnv,
    setErrorCode,
    setErrorMessage,
    verifyDevice,
    invalidCodeError,
  ]);

  return null;
};

const Index: NextPage<{
  screenHint: ScreenHint | undefined;
  children: React.ReactNode;
}> = ({ screenHint }) => {
  const router = useRouter();

  useEffect(() => {
    if (router.pathname === '/') {
      router.push('/code', undefined, { shallow: true });
    }
  }, [router.pathname, router]);

  return (
    <ErrorProvider>
      <OptimizelyProvider>
        <ViewProvider screenHint={screenHint ?? 'Logging_In'}>
          <OauthProvider>
            <ActivationRouteOnError />
            <LogoutCookieProvider>
              <ActivationView />
            </LogoutCookieProvider>
          </OauthProvider>
        </ViewProvider>
      </OptimizelyProvider>
    </ErrorProvider>
  );
};

export default Index;
